import type { PlanData } from './dealCalculations'
import { formatCurrency } from './format-currency'
import { buildAnnexServicesColumnsHtml } from './pdfAnnexServices'

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function descriptionHtml(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>')
}

export type AnnexContractedItem = {
  nome: string
  loyalty?: string
  payment?: string
  investment?: string
}

const ANNEX_STYLES = `
  * { margin:0; padding:0; box-sizing:border-box }
  body { font-family: 'Segoe UI', Arial, sans-serif; color:#333333; background:#fff; padding:0; margin:0 }
  .page { width:100%; max-width:794px; min-height:1123px; padding:12mm 14mm 16mm 14mm; margin:0 auto; background:#fff; display:flex; flex-direction:column; position:relative }
  .title { text-align:center; font-size:18pt; font-weight:800; margin-top:0; color:#111 }
  .plan-title { margin-top:2mm; margin-bottom:3mm; text-align:center }
  .plan-title h2 { font-size:13pt; color:#444; font-weight:600 }
  .description { margin-bottom:4mm; text-align:center; color:#666666; font-size:9pt; line-height:1.4; max-width:92%; margin-left:auto; margin-right:auto; white-space:pre-line }
  .bonus-box { background:#f0f9ff; border-left:4px solid #72C063; padding:3mm 4mm; margin:3mm 0 4mm 0; font-size:9pt; color:#111; text-align:left; width:100% }
  .services-grid { display:flex; gap:10mm; width:100%; flex:1 1 auto; align-items:flex-start; margin-top:2mm }
  .services-col { flex:1; min-width:0; width:50% }
  .category-block { margin-bottom:6mm; break-inside:avoid; page-break-inside:avoid }
  .section-title { font-size:11pt; font-weight:800; color:#000; margin:0 0 2mm 0; border-bottom:1.5px solid #000; padding-bottom:0.5mm; display:inline-block }
  .service-item { display:flex; align-items:flex-start; margin-bottom:1.5mm; break-inside:avoid }
  .service-icon { width:4.5mm; height:4.5mm; margin-right:2mm; display:flex; align-items:center; justify-content:center; margin-top:0.5mm }
  .service-svg { width:100%; height:100%; display:block }
  .service-content { flex-grow:1; font-size:9.5pt; line-height:1.3 }
  .service-name { font-weight:500; color:#333 }
  .service-detail { color:#777; font-size:8.5pt; font-style:italic; margin-top:0.5mm }
  .summary-wrap { width:100%; flex:1; display:flex; flex-direction:column; justify-content:center; padding:8mm 0 }
  .summary-section { width:100%; flex-shrink:0 }
  .bar { display:flex; width:100%; background:#72C063; color:#ffffff; border-radius:6px 6px 0 0; overflow:hidden }
  .bar > div { flex:1; padding:3mm 5mm; font-weight:700; font-size:10pt; text-align:center }
  .row { display:flex; width:100%; background:#fff; border:1.5px solid #eee; border-top:none }
  .table { width:100%; border:1.5px solid #eee; border-radius:0 0 8px 8px; overflow:hidden; background:#ffffff; border-top:none }
  .thead { display:flex; width:100%; border-bottom:1.5px solid #eee; font-weight:700; color:#444; background:#fafafa }
  .cell { flex:1; padding:3.5mm 5mm; font-size:10pt; color:#333; text-align:center; min-width:0 }
  .tfoot { display:flex; width:100%; background:#fff }
  .total { color:#72C063; font-weight:800; font-size:11pt }
  .footer-logo { position:absolute; bottom:6mm; left:50%; transform:translateX(-50%); color:#bbb; font-size:8pt; font-weight:600; text-align:center }
  .footer { position:absolute; bottom:6mm; right:12mm; color:#bbb; font-size:8pt }
`

function buildPaymentText(plan: PlanData): string {
  const pricing = plan.pricing
  const monthlyVal = pricing.valorMensal || pricing.valor || 0
  const setupVal = pricing.valorSetup || 0

  if (plan.paymentDetails?.trim()) return plan.paymentDetails

  if (pricing.recorrente) {
    if (setupVal > 0) {
      return `Setup de ${formatCurrency(setupVal)} + mensalidade de ${formatCurrency(monthlyVal)} via boleto ou cartão.`
    }
    return `Mensalidade recorrente de ${formatCurrency(monthlyVal)} via boleto ou cartão.`
  }
  return `Investimento total parcelado conforme negociação.`
}

function buildInvestmentText(plan: PlanData): string {
  const pricing = plan.pricing
  const total = pricing.total || pricing.valor || 0
  const monthlyVal = pricing.valorMensal || pricing.valor || 0
  if (pricing.recorrente && monthlyVal > 0 && total <= monthlyVal * 1.5) {
    return `${formatCurrency(monthlyVal)}/mês`
  }
  return formatCurrency(total)
}

export function buildAnnexSummarySectionHtml(
  plan: PlanData,
  contractedItems: AnnexContractedItem[],
): string {
  const pricing = plan.pricing
  const planDuration = pricing.duracao || 12
  const showFidelity = plan.loyaltyDuration || planDuration > 0
  const fidelityLabel =
    String(plan.id || '').includes('site') || String(plan.id || '').includes('layout')
      ? 'Prazo para desenvolvimento'
      : 'Fidelidade'

  const defaultLoyalty =
    plan.loyaltyDuration || (showFidelity ? `Fidelidade: ${planDuration} meses` : '')

  const items =
    contractedItems.length > 0
      ? contractedItems
      : [{ nome: plan.nome, loyalty: defaultLoyalty }]

  const multiItemPricing =
    items.length > 1 && items.some((item) => item.payment || item.investment)

  const rowsHtml = items
    .map(
      (item) => `
        <div class="row">
          <div class="cell" style="font-weight:600">${escapeHtml(item.nome)}</div>
          <div class="cell">${showFidelity ? escapeHtml(item.loyalty || defaultLoyalty) : ''}</div>
        </div>`,
    )
    .join('')

  const paymentCell = multiItemPricing
    ? items
        .map(
          (item) =>
            `<div style="margin-bottom:2mm"><strong>${escapeHtml(item.nome)}:</strong> ${escapeHtml(item.payment || buildPaymentText(plan))}</div>`,
        )
        .join('')
    : escapeHtml(buildPaymentText(plan))

  const investmentCell = multiItemPricing
    ? items
        .map(
          (item) =>
            `<div style="margin-bottom:2mm"><strong>${escapeHtml(item.nome)}:</strong> ${escapeHtml(item.investment || buildInvestmentText(plan))}</div>`,
        )
        .join('')
    : escapeHtml(buildInvestmentText(plan))

  return `
      <div class="summary-section">
        <div class="bar">
          <div>Serviços disponíveis</div>
          ${showFidelity ? `<div>${fidelityLabel}</div>` : '<div></div>'}
        </div>
        ${rowsHtml}
        <div class="table">
          <div class="thead">
            <div class="cell">Condições de pagamento</div>
            <div class="cell">Investimento</div>
          </div>
          <div class="tfoot">
            <div class="cell">${paymentCell}</div>
            <div class="cell total">${investmentCell}</div>
          </div>
        </div>
      </div>`
}

function pageShell(body: string, pageLabel: string): string {
  return `<!DOCTYPE html>
  <html lang="pt-BR">
  <head><meta charset="UTF-8" /><style>${ANNEX_STYLES}</style></head>
  <body>
    <div class="page">
      ${body}
      <div class="footer-logo">agradadigital.</div>
      <div class="footer">${pageLabel}</div>
    </div>
  </body>
  </html>`
}

export function generateAnnexScopePageHTML(plan: PlanData, empresaNome: string, pageLabel = '1/2'): string {
  const categoriesHtml = buildAnnexServicesColumnsHtml(plan.servicos, plan.layoutConfig)

  const body = `
      <div class="title">ANEXO I</div>
      <div class="plan-title"><h2>${escapeHtml(plan.nome)} — Proposta para ${escapeHtml(empresaNome)}</h2></div>
      <div class="description">${descriptionHtml(plan.descricao || 'Escopo detalhado de serviços e investimentos propostos.')}</div>
      ${plan.showBonus && plan.bonusText ? `<div class="bonus-box"><strong>Bônus:</strong> ${escapeHtml(plan.bonusText)}</div>` : ''}
      ${categoriesHtml}`

  return pageShell(body, pageLabel)
}

export function generateAnnexSummaryPageHTML(
  plan: PlanData,
  contractedItems: AnnexContractedItem[],
  pageLabel = '2/2',
): string {
  const summaryHtml = buildAnnexSummarySectionHtml(plan, contractedItems)
  const body = `
      <div class="title">ANEXO I</div>
      <div class="summary-wrap">${summaryHtml}</div>`

  return pageShell(body, pageLabel)
}
